import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, TrendingUp, PlusCircle, CheckCircle } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';

/**
 * HomeTasteSkill - Alternative Startseite basierend auf TasteSkill-Prinzipien
 * 
 * Design Read: B2B Marketplace Landing for business buyers (GmbH purchase/sale),
 * with a Modern Corporate/Premium Business language, leaning toward Tailwind utilities + Inter + restrained motion.
 * 
 * Dials: DESIGN_VARIANCE: 5 | MOTION_INTENSITY: 4 | VISUAL_DENSITY: 4
 * 
 * TasteSkill-Verbesserungen gegenüber Original:
 * 1. Asymmetrischer Hero (Left-aligned content / right visual area)
 * 2. 3-Column-Equal-Cards ersetzt durch gestaffeltes 2+1 Layout
 * 3. Decorative Dots entfernt (durch Pipes ersetzt)
 * 4. Button-Alignment korrigiert (alle CTAs bottom-aligned)
 * 5. Mehr visuelles Interest durch subtile Hintergründe
 * 6. Verbesserte Spacing und Hierarchie
 */

const HomeTasteSkill = () => {
  const options = [
    {
      title: 'Bestehende GmbH kaufen',
      description:
        'Finde eine passende Bestands-GmbH und starte schneller mit einer bereits eingetragenen Gesellschaft.',
      icon: Building2,
      href: '/marktplatz',
      button: 'Zum Marktplatz',
      primary: false,
    },
    {
      title: 'GmbH zum Verkauf anbieten',
      description:
        'Reiche deine Bestands-GmbH diskret ein. Wir prüfen die bereitgestellten Angaben auf Plausibilität und bringen dich mit qualifizierten Interessenten zusammen.',
      icon: TrendingUp,
      href: '/seller-onboarding',
      button: 'Jetzt anbieten',
      primary: true,
    },
    {
      title: 'Neue GmbH gründen',
      description:
        'Starte direkt das Formular. Wir begleiten dich von der Vorbereitung bis zur Eintragung - strukturiert und transparent.',
      icon: PlusCircle,
      href: '/onboarding',
      button: 'Formular starten',
      primary: false,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero - Asymmetric Split Layout (TasteSkill: Anti-Center Bias) */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-[#F8FAFA] pointer-events-none" />
        <div className="absolute right-0 top-0 w-1/2 h-full bg-[#F8FAFA] hidden lg:block" style={{ clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0% 100%)' }} />
        
        <div className="max-w-[1320px] mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left: Content (7 columns) */}
            <div className="lg:col-span-7">
              <p className="text-sm font-semibold text-[#009E9A] tracking-wide uppercase mb-5">
                Unternehmensvermittlung
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-[52px] font-bold text-[#0B1930] mb-6 leading-[1.08] tracking-tight">
                Kaufen. Verkaufen.<br />
                Gründen.
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-xl">
                Ob Kauf, Verkauf oder Gründung - wir begleiten Dich persönlich und strukturiert bei Deinem Vorhaben.
              </p>
              
              {/* Trust indicators - Pipes statt Dots (TasteSkill: No decorative dots) */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-10">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#009E9A]" />
                  Diskrete Vermittlung
                </span>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#009E9A]" />
                  Persönliche Begleitung
                </span>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <span className="hidden sm:flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#009E9A]" />
                  Strukturierter Prozess
                </span>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/marktplatz">
                  <Button className="bg-[#0B1930] hover:bg-[#162844] text-white text-[15px] font-semibold px-7 py-3 rounded-lg transition-all hover:translate-y-[-1px]">
                    Marktplatz entdecken
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button variant="outline" className="border-gray-300 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-[15px] font-medium px-7 py-3 rounded-lg transition-all">
                    Beratung anfragen
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Visual Area (5 columns) - Abstract business illustration */}
            <div className="lg:col-span-5 hidden lg:flex items-center justify-center">
              <div className="relative w-full max-w-md aspect-square">
                {/* Abstract geometric shapes representing business/corporate */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-64 h-64">
                    {/* Main building shape */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-48 bg-[#0B1930] rounded-t-lg" />
                    {/* Secondary building */}
                    <div className="absolute bottom-0 left-4 w-20 h-32 bg-[#009E9A] rounded-t-lg" />
                    {/* Third building */}
                    <div className="absolute bottom-0 right-4 w-16 h-24 bg-[#0B1930]/60 rounded-t-lg" />
                    {/* Accent circle */}
                    <div className="absolute top-4 right-8 w-12 h-12 bg-[#E9F7F6] rounded-full border-2 border-[#009E9A]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Gestaffeltes Layout statt 3-Equal-Cards (TasteSkill Core Rule) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F8FAFA]">
        <div className="max-w-[1320px] mx-auto">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1930] mb-3">
              Drei Wege zum Ziel
            </h2>
            <p className="text-gray-600 max-w-xl">
              Wähle den passenden Service für dein Vorhaben.
            </p>
          </div>

          {/* Asymmetric 2+1 Layout (TasteSkill: Break the 3-equal pattern) */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* First two options - larger */}
            {options.slice(0, 2).map((opt) => {
              const Icon = opt.icon;
              return (
                <div
                  key={opt.title}
                  className="bg-white border border-gray-200 rounded-lg p-8 hover:border-[#009E9A] hover:shadow-lg transition-all group flex flex-col"
                >
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 bg-[#E9F7F6] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#009E9A] transition-colors">
                      <Icon className="w-6 h-6 text-[#009E9A] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0B1930] mb-2">{opt.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{opt.description}</p>
                    </div>
                  </div>
                  <div className="mt-auto pt-4">
                    <Link to={opt.href}>
                      <Button
                        className={`w-full justify-center rounded-lg text-[15px] py-3 transition-all hover:translate-y-[-1px] ${
                          opt.primary
                            ? 'bg-[#0B1930] hover:bg-[#162844] text-white'
                            : 'bg-white border border-gray-300 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A]'
                        }`}
                      >
                        {opt.button}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Third option - full width accent */}
          <div className="bg-[#0B1930] rounded-lg p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <PlusCircle className="w-6 h-6 text-[#009E9A]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{options[2].title}</h3>
                  <p className="text-gray-300 leading-relaxed max-w-xl">{options[2].description}</p>
                </div>
              </div>
              <Link to={options[2].href} className="flex-shrink-0">
                <Button className="bg-white text-[#0B1930] hover:bg-[#E9F7F6] rounded-lg text-[15px] px-6 py-3 transition-all hover:translate-y-[-1px]">
                  {options[2].button}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6]">
        <div className="max-w-[800px] mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      {/* Contact CTA - Clean, minimal */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="text-base md:text-lg text-gray-600 mb-6">
            Unsicher, welcher Weg der richtige ist? Schreib uns - wir helfen schnell weiter.
          </p>
          <Link to="/kontakt">
            <Button className="bg-[#0B1930] hover:bg-[#162844] text-white px-7 py-3 text-[15px] rounded-lg transition-all hover:translate-y-[-1px]">
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomeTasteSkill;
